import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import useLogisticsApi, {
  getOfflineQueue,
  saveOfflineQueue,
  recordFailure,
} from './use-logistics-api';
import { ApiError } from '../utils/api-error';
import { classifyQueueFailure, replayedBoxIdOf } from '../utils/queue-failure-policy';
import { remapQueueIds } from '../utils/queue-id-remap';
import type { CourierRoutesResult } from './use-logistics-api';
import useOCR from './use-ocr';

import { API_ENDPOINTS, IS_DEBUG } from '../config/api';
import { getBlob, deleteBlobs } from '../utils/offline-blob-store';
import {
  QueuedEvent,
  StatusUpdateRequest,
  SignatureQueuePayload,
  AddBoxQueuePayload,
  RemoveBoxQueuePayload,
  RouteOrderQueuePayload,
  SignatureRequest,
  AddBoxToOrderResponse,
  Order,
  OrderBox,
  BatchSyncResponse,
} from '../types/logistics';

const MAX_ATTEMPTS: Partial<Record<QueuedEvent['type'], number>> = {
  ADD_BOX: 5,
  REMOVE_BOX: 5,
  SIGNATURE: 3,
  ROUTE_ORDER: 3,
};

// Module-level sync lock: prevents Connectivity + useLayoutServices from racing each other.
// Each hook instance has its own ref, which wouldn't stop cross-instance duplication.
let globalSyncInFlight = false;

export interface OfflineSyncResult {
  processed: number;
  failed: number;
  dropped: number;
}

/**
 * Orchestrates offline queue drain for all event types.
 *
 * Order:
 *   1. Coalesce ROUTE_ORDER and SIGNATURE duplicates.
 *   2. Drain ADD_BOX sequentially; capture tempId → realId map; rewrite queue + React Query cache.
 *   3. Drain STATUS_CHANGE + OCR via the existing `syncOfflineQueue()` (UNCHANGED batch path).
 *   4. Drain REMOVE_BOX, SIGNATURE, ROUTE_ORDER via individual REST endpoints.
 */
const useOfflineSync = () => {
  const { request, syncOfflineQueue } = useLogisticsApi();
  const { syncOCRQueue } = useOCR();
  const queryClient = useQueryClient();
  const inFlight = useRef(false);

  const rewriteQueueIds = useCallback((idMap: Map<number, number>) => {
    const queue = getOfflineQueue();
    if (remapQueueIds(queue, idMap)) saveOfflineQueue(queue);
  }, []);

  const rewriteQueryCache = useCallback(
    (idMap: Map<number, number>) => {
      if (idMap.size === 0) return;
      queryClient.setQueriesData<CourierRoutesResult>(
        { queryKey: ['courier-routes'] },
        (prev) => {
          if (!prev) return prev;
          let mutated = false;
          const orders = prev.orders.map((o: Order) => {
            if (!o.orderBoxes) return o;
            let orderMutated = false;
            const newBoxes = o.orderBoxes.map((b: OrderBox) => {
              const real = idMap.get(b.id);
              if (real !== undefined) {
                orderMutated = true;
                return { ...b, id: real };
              }
              return b;
            });
            if (orderMutated) {
              mutated = true;
              return { ...o, orderBoxes: newBoxes };
            }
            return o;
          });
          return mutated ? { ...prev, orders } : prev;
        }
      );
    },
    [queryClient]
  );

  const coalesceQueue = useCallback(() => {
    const queue = getOfflineQueue();
    let changed = false;

    // ROUTE_ORDER last-write-wins per date
    const latestRouteOrder = new Map<string, QueuedEvent>();
    queue.forEach((e) => {
      if (e.type === 'ROUTE_ORDER') {
        const p = e.payload as RouteOrderQueuePayload;
        const existing = latestRouteOrder.get(p.date);
        if (!existing || (existing.payload as RouteOrderQueuePayload).clientTimestamp < p.clientTimestamp) {
          latestRouteOrder.set(p.date, e);
        }
      }
    });

    // SIGNATURE last-write-wins per (orderId, isCollection)
    const latestSignature = new Map<string, QueuedEvent>();
    queue.forEach((e) => {
      if (e.type === 'SIGNATURE') {
        const p = e.payload as SignatureQueuePayload;
        const key = `${p.orderId}|${p.isCollection}`;
        const existing = latestSignature.get(key);
        if (!existing || (existing.payload as SignatureQueuePayload).clientTimestamp < p.clientTimestamp) {
          latestSignature.set(key, e);
        }
      }
    });

    const droppedBlobRefs: string[] = [];
    const kept = queue.filter((e) => {
      if (e.type === 'ROUTE_ORDER') {
        if (latestRouteOrder.get((e.payload as RouteOrderQueuePayload).date) !== e) {
          changed = true;
          return false;
        }
      } else if (e.type === 'SIGNATURE') {
        const p = e.payload as SignatureQueuePayload;
        if (latestSignature.get(`${p.orderId}|${p.isCollection}`) !== e) {
          changed = true;
          if (e.blobRefs) droppedBlobRefs.push(...e.blobRefs);
          return false;
        }
      }
      return true;
    });

    if (changed) {
      saveOfflineQueue(kept);
      if (droppedBlobRefs.length > 0) deleteBlobs(droppedBlobRefs).catch(() => undefined);
    }
  }, []);

  const drainAddBoxes = useCallback(async (): Promise<Map<number, number>> => {
    const idMap = new Map<number, number>();
    // Snapshot only ADD_BOX events, drain sequentially
    const snapshot = getOfflineQueue().filter((e) => e.type === 'ADD_BOX');

    for (const event of snapshot) {
      const payload = event.payload as AddBoxQueuePayload;
      try {
        const response = await request<AddBoxToOrderResponse>(
          API_ENDPOINTS.ORDER_ADD_BOX(payload.orderId),
          {
            method: 'POST',
            body: JSON.stringify({
              barcode: payload.barcode,
              notes: payload.notes,
              latitude: payload.latitude,
              longitude: payload.longitude,
            }),
          }
        );
        const realId = response.orderBox.id;
        idMap.set(payload.tempBoxId, realId);

        // Remove this event + immediately rewrite queue so later loop iterations see rewritten ids
        const queue = getOfflineQueue().filter((e) => e.id !== event.id);
        saveOfflineQueue(queue);
        rewriteQueueIds(new Map([[payload.tempBoxId, realId]]));
      } catch (err: any) {
        // This drain was the one the 2026-09-27 pass did not migrate, so until now it
        // still decided everything inline -- including a fifth copy of the
        // session-expired string, compared with `===`. The decisions live in
        // classifyQueueFailure with the rest.
        const verdict = classifyQueueFailure('ADD_BOX', err);
        if (verdict === 'abort') throw err;

        // Dropping an ADD_BOX means the temp id it was going to resolve never will, so
        // whatever was queued against that id has to go with it or it retries forever
        // against a box that does not exist.
        const withoutEventAndDependents = (queue: QueuedEvent[]): QueuedEvent[] =>
          queue.filter((e) => {
            if (e.id === event.id) return false;
            if (e.type === 'STATUS_CHANGE') {
              return (e.payload as StatusUpdateRequest).orderBoxId !== payload.tempBoxId;
            }
            if (e.type === 'REMOVE_BOX') {
              return (e.payload as RemoveBoxQueuePayload).orderBoxId !== payload.tempBoxId;
            }
            return true;
          });

        // A replay of a request that got through: the box is on the order, and the only
        // thing the phone was missing is the real id -- which the conflict carries. So
        // the remap still happens and everything queued behind it goes through.
        const replayedId = replayedBoxIdOf(err);
        if (verdict === 'succeeded' && replayedId !== undefined) {
          idMap.set(payload.tempBoxId, replayedId);
          saveOfflineQueue(getOfflineQueue().filter((e) => e.id !== event.id));
          rewriteQueueIds(new Map([[payload.tempBoxId, replayedId]]));
          continue;
        }

        // The same barcode on somebody else's order. Retrying cannot resolve that, and
        // it is exactly the case a courier needs to see, so it goes straight to the log.
        if (verdict === 'drop') {
          saveOfflineQueue(withoutEventAndDependents(getOfflineQueue()));
          recordFailure({
            ...event,
            attempts: event.attempts + 1,
            lastError: err.message || String(err),
            failureCode: err instanceof ApiError ? err.code : undefined,
            requestId: err instanceof ApiError ? err.requestId : undefined,
          });
          continue;
        }

        // Bump attempts; drop + record if maxed out
        const queue = getOfflineQueue();
        const live = queue.find((e) => e.id === event.id);
        if (live) {
          live.attempts++;
          live.lastError = err.message || String(err);
          if (err instanceof ApiError) {
            live.failureCode = err.code;
            live.requestId = err.requestId;
          }
          const max = MAX_ATTEMPTS.ADD_BOX ?? 5;
          if (live.attempts >= max) {
            saveOfflineQueue(withoutEventAndDependents(queue));
            recordFailure(live);
          } else {
            saveOfflineQueue(queue);
          }
        }
      }
    }

    rewriteQueryCache(idMap);
    return idMap;
  }, [request, rewriteQueueIds, rewriteQueryCache]);

  const drainByType = useCallback(
    async (
      type: QueuedEvent['type'],
      processOne: (event: QueuedEvent) => Promise<void>
    ): Promise<{ processed: number; failed: number; dropped: number }> => {
      let processed = 0;
      let failed = 0;
      let dropped = 0;
      const snapshot = getOfflineQueue().filter((e) => e.type === type);
      for (const event of snapshot) {
        try {
          await processOne(event);
          // Success: drop event + delete any blobs
          const queue = getOfflineQueue().filter((e) => e.id !== event.id);
          saveOfflineQueue(queue);
          if (event.blobRefs && event.blobRefs.length > 0) {
            deleteBlobs(event.blobRefs).catch(() => undefined);
          }
          processed++;
        } catch (err: any) {
          // The decision lives in classifyQueueFailure, which reads ApiError.httpStatus.
          // This used to match /\b409\b/ against err.message, and the message is the
          // backend's human sentence -- "This box has already been scanned" -- so neither
          // special case below ever fired. See queue-failure-policy.ts.
          const verdict = classifyQueueFailure(type, err);
          if (verdict === 'abort') throw err;

          if (verdict === 'succeeded') {
            const queue = getOfflineQueue().filter((e) => e.id !== event.id);
            saveOfflineQueue(queue);
            processed++;
            continue;
          }

          if (verdict === 'drop') {
            const remaining = getOfflineQueue().filter((e) => e.id !== event.id);
            saveOfflineQueue(remaining);
            recordFailure({
              ...event,
              attempts: event.attempts + 1,
              lastError: err.message || String(err),
              failureCode: err instanceof ApiError ? err.code : undefined,
              requestId: err instanceof ApiError ? err.requestId : undefined,
            });
            if (event.blobRefs && event.blobRefs.length > 0) {
              deleteBlobs(event.blobRefs).catch(() => undefined);
            }
            dropped++;
            continue;
          }

          const queue = getOfflineQueue();
          const live = queue.find((e) => e.id === event.id);
          if (!live) continue;

          live.attempts++;
          live.lastError = err.message || String(err);
          if (err instanceof ApiError) {
            live.failureCode = err.code;
            live.requestId = err.requestId;
          }
          const max = MAX_ATTEMPTS[type] ?? 3;
          if (live.attempts >= max) {
            const remaining = queue.filter((e) => e.id !== event.id);
            saveOfflineQueue(remaining);
            recordFailure(live);
            if (live.blobRefs && live.blobRefs.length > 0) {
              deleteBlobs(live.blobRefs).catch(() => undefined);
            }
            dropped++;
          } else {
            saveOfflineQueue(queue);
            failed++;
          }
        }
      }
      return { processed, failed, dropped };
    },
    []
  );

  const runSync = useCallback(async (): Promise<OfflineSyncResult | null> => {
    if (inFlight.current || globalSyncInFlight) return null;
    if (!navigator.onLine) return null;
    inFlight.current = true;
    globalSyncInFlight = true;

    try {
      coalesceQueue();

      const totals: OfflineSyncResult = { processed: 0, failed: 0, dropped: 0 };

      // Phase 1: Drain ADD_BOX (must come before STATUS_CHANGE/REMOVE_BOX that reference tempIds)
      await drainAddBoxes();

      // Phase 2: Drain STATUS_CHANGE + OCR via existing batch endpoints (UNCHANGED path)
      const batchResult: BatchSyncResponse | null = await syncOfflineQueue();
      if (batchResult) {
        totals.processed += batchResult.processed;
        totals.failed += batchResult.failed.length;
      }

      // Phase 3: Drain REMOVE_BOX
      const removeRes = await drainByType('REMOVE_BOX', async (event) => {
        const p = event.payload as RemoveBoxQueuePayload;
        await request(API_ENDPOINTS.ORDER_REMOVE_BOX(p.orderId, p.orderBoxId), {
          method: 'DELETE',
          body: JSON.stringify({
            reason: p.reason,
            latitude: p.latitude,
            longitude: p.longitude,
          }),
        });
      });
      totals.processed += removeRes.processed;
      totals.failed += removeRes.failed;
      totals.dropped += removeRes.dropped;

      // Phase 4: Drain SIGNATURE (rehydrate blobs from IndexedDB)
      const sigRes = await drainByType('SIGNATURE', async (event) => {
        const p = event.payload as SignatureQueuePayload;
        const signatureUrl = await getBlob(p.signatureBlobRef);
        if (!signatureUrl) {
          throw new Error('Signature blob missing from IndexedDB');
        }
        const proofPhotoBase64 = p.proofPhotoBlobRef ? await getBlob(p.proofPhotoBlobRef) : null;
        const payload: SignatureRequest = {
          signatureUrl,
          isCollection: p.isCollection,
          ...(proofPhotoBase64 ? { proofPhotoBase64 } : {}),
        };
        await request(API_ENDPOINTS.ORDER_SIGNATURE(p.orderId), {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      });
      totals.processed += sigRes.processed;
      totals.failed += sigRes.failed;
      totals.dropped += sigRes.dropped;

      // Phase 5: Drain ROUTE_ORDER
      const routeRes = await drainByType('ROUTE_ORDER', async (event) => {
        const p = event.payload as RouteOrderQueuePayload;
        await request(API_ENDPOINTS.COURIER_ROUTE_ORDER(p.date), {
          method: 'PUT',
          body: JSON.stringify({ stopOrder: p.stopOrder }),
        });
      });
      totals.processed += routeRes.processed;
      totals.failed += routeRes.failed;
      totals.dropped += routeRes.dropped;

      // Phase 6: Kick OCR sync if any remain (in case syncOfflineQueue didn't cover newly added)
      try {
        await syncOCRQueue();
      } catch {
        // OCR failures shouldn't block main sync result
      }

      if (IS_DEBUG) console.log('[OfflineSync] complete', totals);
      return totals;
    } finally {
      inFlight.current = false;
      globalSyncInFlight = false;
    }
  }, [coalesceQueue, drainAddBoxes, drainByType, request, syncOfflineQueue, syncOCRQueue]);

  return { runSync };
};

export default useOfflineSync;
